/*
 * The Production screen: each good's made and used a minute over the
 * last ten minutes, hour or two, on one axis. Goods are toggled on the
 * left (the legend too); made is solid, used dashed, in the good's hue;
 * hovering reads out every shown good at that moment.
 */

import { For, Show, createMemo, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import { TICKS_PER_MINUTE, goodHue, goodsNow, historyGoods, rate, samples, series } from "../state/history";
import { GoodIcon, RecipeIcon } from "../console/RecipePicker";
import { idleGroups, selectIdle } from "../state/idle";
import { UNLINKED_SVG } from "../ui/goodIcons";

/** A good's icon by the name shown here (rounds are munitions). */
const Icon = (p: { good: string; size?: number }) => <GoodIcon good={p.good === "Rounds" ? "Munitions" : p.good} size={p.size ?? 18} />;

// The list (Factorio's production statistics) or the graphs.
const [view, setView] = createSignal<"list" | "graphs">("list");
// The list's order: by what's made, used, or the net.
const [sortBy, setSortBy] = createSignal<"made" | "used" | "net" | "stock">("made");

/** What's stuck: your idle plants by recipe and reason, the good they
 *  wait on (or can't send away); a click selects them on the map. */
function IdleList(): JSX.Element {
  return (
    <Show when={idleGroups().length}>
      <h3 class="production-sub">Idle plants</h3>
      <table class="production-list idle-list">
        <tbody>
          <For each={idleGroups()}>
            {(g) => (
              <tr class="production-row" onClick={() => selectIdle(g)} data-tip="Select them on the map">
                <td>
                  <span class="ai-good">
                    <RecipeIcon recipe={g.recipe} size={20} />
                    {g.recipe}
                  </span>
                </td>
                <td class="num">
                  {g.idle} of {g.total} idle
                </td>
                <td>
                  <span class="ai-good idle-why">
                    <Show when={g.link}>
                      <span class="good-icon-wrap" innerHTML={UNLINKED_SVG(18)} />
                    </Show>
                    <Show when={g.good}>{(x) => <Icon good={x()} size={18} />}</Show>
                    {g.long ?? g.why}
                  </span>
                </td>
              </tr>
            )}
          </For>
        </tbody>
      </table>
      <h3 class="production-sub">Goods</h3>
    </Show>
  );
}

/** Every good your industry makes, uses or holds: a row each with its
 *  icon, stock, made, used and net a minute, and its last hour; a click
 *  opens its graph. */
function GoodsList(p: { open: (g: string) => void }): JSX.Element {
  const rows = createMemo(() => {
    const names = historyGoods();
    const list = goodsNow()
      .map((g) => ({ name: names[g.good] === "Munitions" ? "Rounds" : (names[g.good] ?? `Good ${g.good}`), stock: g.stock, made: g.made_per_min, used: g.used_per_min, net: g.made_per_min - g.used_per_min }))
      .filter((r) => r.stock > 0 || r.made > 0 || r.used > 0);
    const k = sortBy();
    return list.sort((a, b) => (k === "net" ? Math.abs(b.net) - Math.abs(a.net) : b[k] - a[k]) || a.name.localeCompare(b.name));
  });
  // The sparklines' span: an hour, or what there is since the server's
  // last restart.
  const span = () => {
    const sm = samples();
    const m = sm.length > 1 ? Math.round((sm[sm.length - 1].tick - sm[0].tick) / TICKS_PER_MINUTE) : 0;
    return m >= 60 ? "Last hour" : `Last ${Math.max(1, m)} min`;
  };
  const head = (k: "made" | "used" | "net" | "stock", label: string, tip: string) => (
    <th class="num" aria-sort={sortBy() === k ? "descending" : undefined}>
      <button class="th-sort" data-tip={tip} onClick={() => setSortBy(k)}>
        {label}
      </button>
    </th>
  );
  return (
    <Show when={rows().length} fallback={<p class="muted">Nothing made or held yet: build a mine and a plant.</p>}>
      <table class="production-list">
        <thead>
          <tr>
            <th>Good</th>
            {head("stock", "Held", "Held across your stations and depots")}
            {head("made", "Made", "Made a minute, across your industry")}
            {head("used", "Used", "Used a minute: plants, barracks, upkeep")}
            {head("net", "Net", "Made less used, a minute")}
            <th data-tip="Made (solid) and used (dashed); the server keeps two hours, from its last restart">{span()}</th>
          </tr>
        </thead>
        <tbody>
          <For each={rows()}>
            {(r) => (
              <tr class="production-row" onClick={() => p.open(r.name)} data-tip={`${r.name}: its graph`}>
                <td>
                  <span class="ai-good">
                    <Icon good={r.name} />
                    {r.name}
                  </span>
                </td>
                <td class="num">{rate(r.stock)}</td>
                <td class="num up">{r.made ? `+${rate(r.made)}` : ""}</td>
                <td class="num down">{r.used ? `−${rate(r.used)}` : ""}</td>
                <td class="num" classList={{ up: r.net > 0.5, down: r.net < -0.5 }}>
                  {Math.abs(r.net) < 0.5 ? "0" : `${r.net > 0 ? "+" : "−"}${rate(Math.abs(r.net))}`}
                </td>
                <td>
                  <Spark good={r.name} />
                </td>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </Show>
  );
}

const RANGES = [
  { label: "10 min", minutes: 10 },
  { label: "1 h", minutes: 60 },
  { label: "2 h", minutes: 120 },
];

// What's shown, kept while the screen is closed and opened again.
const [chosen, setChosen] = createSignal<string[] | null>(null);
const [range, setRange] = createSignal(60);

/** A round step for about four gridlines up to `max`. */
function step(max: number): number {
  const raw = max / 4;
  const p = 10 ** Math.floor(Math.log10(Math.max(1e-9, raw)));
  return [1, 2, 5, 10].map((m) => m * p).find((s) => s >= raw) ?? p * 10;
}

// Together on one axis, or apart (a small chart each, its own scale):
// rounds run in the thousands while ores run in the hundreds, so on one
// axis everything but rounds lies flat. Null: chosen by the spread.
const [layout, setLayout] = createSignal<"together" | "apart" | null>(null);
// The moment hovered (a sample's tick), shared by every chart.
const [hover, setHover] = createSignal<number | null>(null);

// The window: the last `range` minutes, up to the latest sample.
const last = () => samples()[samples().length - 1]?.tick ?? 0;
const from = () => last() - range() * TICKS_PER_MINUTE;
const at = (g: string, tick: number) => series(g)?.find((p) => p.tick === tick);
const ago = (tick: number) => {
  const m = Math.round((last() - tick) / TICKS_PER_MINUTE);
  return m <= 0 ? "Now" : `${m} min ago`;
};

/** One chart of some goods' made (solid) and used (dashed) a minute, on one
 *  axis, over the window; hovering picks a moment for every chart. */
function Chart(p: { goods: string[]; width: number; height: number; compact?: boolean }): JSX.Element {
  const pad = () => (p.compact ? { left: 40, right: 8, top: 8, bottom: 20 } : { left: 52, right: 16, top: 12, bottom: 26 });
  const lines = createMemo(() => p.goods.map((g) => ({ good: g, points: (series(g) ?? []).filter((q) => q.tick >= from()) })));
  const max = () => Math.max(1, ...lines().flatMap((l) => l.points.flatMap((q) => [q.made, q.used])));
  const top = () => Math.ceil(max() / step(max())) * step(max());
  const x = (tick: number) => pad().left + ((tick - from()) / (range() * TICKS_PER_MINUTE)) * (p.width - pad().left - pad().right);
  const y = (v: number) => pad().top + (1 - v / top()) * (p.height - pad().top - pad().bottom);
  const path = (pts: { tick: number; v: number }[]) => pts.map((q) => `${x(q.tick).toFixed(1)},${y(q.v).toFixed(1)}`).join(" ");
  const yTicks = () => {
    const st = step(max());
    return Array.from({ length: Math.round(top() / st) + 1 }, (_, i) => i * st).filter((_, i, a) => !p.compact || i === 0 || i === a.length - 1 || i % 2 === 0);
  };
  const xTicks = () => {
    const every = range() <= 10 ? (p.compact ? 5 : 2) : range() <= 60 ? (p.compact ? 30 : 10) : p.compact ? 60 : 20;
    return Array.from({ length: Math.floor(range() / every) + 1 }, (_, i) => i * every);
  };
  const move = (e: PointerEvent) => {
    const r = (e.currentTarget as SVGElement).getBoundingClientRect();
    const t = from() + ((e.clientX - r.left - pad().left) / (p.width - pad().left - pad().right)) * range() * TICKS_PER_MINUTE;
    const ticks = samples().filter((q) => q.tick >= from()).map((q) => q.tick);
    if (!ticks.length) return setHover(null);
    setHover(ticks.reduce((a, b) => (Math.abs(b - t) < Math.abs(a - t) ? b : a)));
  };
  return (
    <svg width={p.width} height={p.height} onPointerMove={move} onPointerLeave={() => setHover(null)}>
      <For each={yTicks()}>
        {(v) => (
          <>
            <line class="grid" x1={pad().left} x2={p.width - pad().right} y1={y(v)} y2={y(v)} />
            <text class="axis" x={pad().left - 6} y={y(v) + 4} text-anchor="end">
              {rate(v)}
            </text>
          </>
        )}
      </For>
      <For each={xTicks()}>
        {(m) => (
          <text class="axis" x={x(last() - m * TICKS_PER_MINUTE)} y={p.height - 6} text-anchor={m === 0 ? "end" : "middle"}>
            {m === 0 ? "now" : `−${m} min`}
          </text>
        )}
      </For>
      <For each={lines()}>
        {(l) => (
          <>
            <polyline class="used" points={path(l.points.map((q) => ({ tick: q.tick, v: q.used })))} stroke={goodHue(l.good)} />
            <polyline class="made" points={path(l.points.map((q) => ({ tick: q.tick, v: q.made })))} stroke={goodHue(l.good)} />
          </>
        )}
      </For>
      <Show when={hover() !== null && hover()! >= from()}>
        <line class="crosshair" x1={x(hover()!)} x2={x(hover()!)} y1={pad().top} y2={p.height - pad().bottom} />
        <For each={lines()}>
          {(l) => {
            const q = () => at(l.good, hover()!);
            return (
              <Show when={q()}>
                <circle cx={x(hover()!)} cy={y(q()!.made)} r="4" fill={goodHue(l.good)} class="mark" />
              </Show>
            );
          }}
        </For>
      </Show>
    </svg>
  );
}

/** A good's made, used and net at a moment (the readout's row). */
function Row(p: { good: string; tick: number }): JSX.Element {
  const q = () => at(p.good, p.tick);
  return (
    <Show when={q()}>
      <div class="row">
        <span class="swatch" style={{ background: goodHue(p.good) }} />
        <span>{p.good}</span>
        <span>+{rate(q()!.made)}</span>
        <span>−{rate(q()!.used)}</span>
        <span classList={{ up: q()!.made > q()!.used, down: q()!.made < q()!.used }}>
          {q()!.made >= q()!.used ? "+" : "−"}
          {rate(Math.abs(q()!.made - q()!.used))}
        </span>
      </div>
    </Show>
  );
}

export function ProductionScreen(): JSX.Element {
  const goods = () => historyGoods().map((g) => (g === "Munitions" ? "Rounds" : g));
  // At first: every good that's moved at all (else steel).
  const shown = () => {
    const c = chosen();
    if (c) return c;
    const moved = goods().filter((g) => series(g)?.some((q) => q.made > 0 || q.used > 0));
    return moved.length ? moved : ["Steel"];
  };
  const toggle = (g: string) => setChosen(shown().includes(g) ? shown().filter((x) => x !== g) : [...shown(), g]);
  // Apart when the goods shown differ more than tenfold (else one axis).
  const peak = (g: string) => Math.max(0, ...(series(g) ?? []).filter((q) => q.tick >= from()).flatMap((q) => [q.made, q.used]));
  const apart = () => {
    const l = layout();
    if (l) return l === "apart";
    const peaks = shown().map(peak).filter((v) => v > 0);
    return peaks.length > 1 && Math.max(...peaks) > 10 * Math.min(...peaks);
  };

  let box!: HTMLDivElement;
  const [width, setWidth] = createSignal(800);
  onMount(() => {
    const ro = new ResizeObserver(() => setWidth(box.clientWidth));
    ro.observe(box);
    onCleanup(() => ro.disconnect());
  });
  // Small charts: as many across as fit at about 320 px.
  const across = () => Math.max(1, Math.floor((width() + 12) / 332));
  const small = () => Math.max(120, Math.floor((width() - (across() - 1) * 12) / across()));

  return (
    <section class="depth-screen production" aria-label="Production">
      <div class="production-layout">
        <aside class="production-goods" aria-label="Goods shown">
          <For each={goods()}>
            {(g) => {
              const now = () => series(g)?.at(-1);
              return (
                <label class="production-good" classList={{ off: !shown().includes(g) }}>
                  <input type="checkbox" checked={shown().includes(g)} onChange={() => toggle(g)} />
                  <Icon good={g} />
                  <span class="name">{g}</span>
                  <span class="now">
                    <Show when={now() && (now()!.made > 0 || now()!.used > 0) ? now() : null}>
                      {(n) => (
                        <>
                          +{rate(n().made)} <span class="muted">−{rate(n().used)}</span>
                        </>
                      )}
                    </Show>
                  </span>
                </label>
              );
            }}
          </For>
          <p class="muted small">A minute's made (solid) and used (dashed), every 30 s.</p>
        </aside>
        <div class="production-main">
          <div class="production-head">
            <h2>Production a minute</h2>
            <div class="production-controls">
              <div class="segmented" role="radiogroup" aria-label="View">
                <button role="radio" aria-checked={view() === "list"} onClick={() => setView("list")}>
                  List
                </button>
                <button role="radio" aria-checked={view() === "graphs"} onClick={() => setView("graphs")}>
                  Graphs
                </button>
              </div>
              <Show when={view() === "graphs"}>
              <div class="segmented" role="radiogroup" aria-label="Layout">
                <button role="radio" aria-checked={!apart()} onClick={() => setLayout("together")} data-tip="Every good on one scale">
                  Together
                </button>
                <button role="radio" aria-checked={apart()} onClick={() => setLayout("apart")} data-tip="A small chart each, each on its own scale">
                  Apart
                </button>
              </div>
              <div class="segmented" role="radiogroup" aria-label="Time range">
                <For each={RANGES}>
                  {(r) => (
                    <button role="radio" aria-checked={range() === r.minutes} onClick={() => setRange(r.minutes)}>
                      {r.label}
                    </button>
                  )}
                </For>
              </div>
              </Show>
            </div>
          </div>
          <Show when={view() === "list"}>
            <IdleList />
            <GoodsList
              open={(g) => {
                setChosen([g]);
                setView("graphs");
              }}
            />
          </Show>
          <div class="production-chart" ref={box} classList={{ hidden: view() !== "graphs" }}>
            <Show when={view() === "graphs" && samples().length >= 2} fallback={<p class="muted">The graphs fill in as your economy runs: a point every 30 seconds.</p>}>
              <Show
                when={apart()}
                fallback={
                  <>
                    <Chart goods={shown()} width={width()} height={340} />
                    <Show when={hover() !== null}>
                      <div class="production-readout" style={{ left: `${Math.min(52 + ((hover()! - from()) / (range() * TICKS_PER_MINUTE)) * (width() - 68) + 12, width() - 230)}px` }}>
                        <div class="when">{ago(hover()!)}</div>
                        <For each={shown()}>{(g) => <Row good={g} tick={hover()!} />}</For>
                      </div>
                    </Show>
                  </>
                }
              >
                <div class="production-grid" style={{ "grid-template-columns": `repeat(${across()}, ${small()}px)` }}>
                  <For each={shown()}>
                    {(g) => {
                      const q = () => (hover() !== null ? at(g, hover()!) : series(g)?.at(-1));
                      return (
                        <div class="production-small">
                          <div class="production-small-head">
                            <span class="swatch" style={{ background: goodHue(g) }} />
                            <span class="name">{g}</span>
                            <Show when={q()}>
                              <span class="muted">
                                {hover() !== null ? `${ago(hover()!)}: ` : ""}+{rate(q()!.made)} −{rate(q()!.used)}
                              </span>
                            </Show>
                          </div>
                          <Chart goods={[g]} width={small() - 10} height={150} compact />
                        </div>
                      );
                    }}
                  </For>
                </div>
              </Show>
            </Show>
          </div>
        </div>
      </div>
    </section>
  );
}

/** A good's last hour as a sparkline: made solid, used dashed, the gap
 *  between them green (making more) or red (using more). */
export function Spark(p: { good: string }): JSX.Element {
  const W = 72;
  const SH = 18;
  const pts = () => {
    const s = series(p.good) ?? [];
    const end = s[s.length - 1]?.tick ?? 0;
    return s.filter((q) => q.tick >= end - 60 * TICKS_PER_MINUTE);
  };
  const geo = () => {
    const ps = pts();
    if (ps.length < 2 || ps.every((q) => q.made <= 0 && q.used <= 0)) return null;
    const max = Math.max(1, ...ps.flatMap((q) => [q.made, q.used]));
    const [t0, t1] = [ps[0].tick, ps[ps.length - 1].tick];
    const x = (t: number) => (1 + ((t - t0) / Math.max(1, t1 - t0)) * (W - 2)).toFixed(1);
    const y = (v: number) => (SH - 1 - (v / max) * (SH - 2)).toFixed(1);
    const bands = ps.slice(1).map((q, i) => {
      const a = ps[i];
      return {
        pts: `${x(a.tick)},${y(a.made)} ${x(q.tick)},${y(q.made)} ${x(q.tick)},${y(q.used)} ${x(a.tick)},${y(a.used)}`,
        up: a.made + q.made >= a.used + q.used,
      };
    });
    return {
      bands,
      made: ps.map((q) => `${x(q.tick)},${y(q.made)}`).join(" "),
      used: ps.map((q) => `${x(q.tick)},${y(q.used)}`).join(" "),
    };
  };
  return (
    <Show when={geo()} fallback={<span />}>
      {(g) => (
        <svg class="spark" width={W} height={SH} aria-hidden="true">
          <For each={g().bands}>{(b) => <polygon points={b.pts} class={b.up ? "up" : "down"} />}</For>
          <polyline class="used" points={g().used} />
          <polyline class="made" points={g().made} />
        </svg>
      )}
    </Show>
  );
}
